/**
 * Engagement evidence — a company's record of what it could see when it
 * decided. (W5.8 · F31 Phase 3)
 *
 * The wire contract (header, item, diff, verdict history, retention notice) is
 * declared ONCE in `@tonnie/foundation` and imported by platform-api and here,
 * so the two ends cannot disagree. What this file adds is the per-kind PAYLOAD
 * typing — each kind's payload is the canonical drawer reader's output, whose
 * types already live in this SDK — so a renderer can narrow on `item.kind`.
 *
 * All routes are business-admin gated server-side.
 */

import type {
  EvidenceBlobUrlView,
  EvidenceCaptureSummary,
  EvidenceCaptureView,
  EvidenceDiffView,
  EvidenceItemView,
  EvidenceKind,
  EvidenceRedactionReason,
  VerdictHistoryView,
} from '@tonnie/foundation';

import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';
import type {
  TalentAdaptationDetail,
  TalentCareDetail,
  TalentCheckupDetail,
  TalentEvidenceDetail,
  TalentMobilityDetail,
} from './business.service';
import type { CheckUpResults } from './checkup.service';
import type { TestitBusinessOverview } from './testit.service';

export type {
  EvidenceBlobUrlView,
  EvidenceCaptureSummary,
  EvidenceCaptureView,
  EvidenceDiffView,
  EvidenceDiffItem,
  EvidenceItemView,
  EvidenceKind,
  EvidenceRedactionReason,
  EvidenceRetentionNotice,
  EvidenceRetentionView,
  VerdictHistoryEntry,
  VerdictHistoryView,
} from '@tonnie/foundation';

// ═══════════════════════════════════════════════════════════════════════
// PAYLOADS — one per kind, the canonical reader's output
// ═══════════════════════════════════════════════════════════════════════

/** `pitch_video` / `cv` (and each `mobility_documents` entry): what the company could see about the file. Never a URL. */
export interface EvidenceFilePayload {
  vaultFileId: string;
  fileName: string;
  mimeType: string | null;
  byteSize: number | null;
  uploadedAt: string;
  /** Were the bytes in storage when read? `false` means nothing is retained. */
  inStorage: boolean;
}

/**
 * One `mobility_documents` entry: the Mobility document as the drawer lists
 * it (`TalentMobilityDetail.documents[].id` is `documentId`), and what the
 * company could see about its file. The file itself is read through
 * `getRetainedBlobUrl` — the item's `blobs` carry one reference per stored file.
 */
export interface EvidenceMobilityDocumentPayload extends EvidenceFilePayload {
  documentId: string;
  documentName: string;
  documentType: string;
}

/**
 * The legacy shape of a TestIt verdict's evidence — what the browser sent
 * before server capture existed. Only on an item with disposition
 * `legacy_client_supplied` (a `backfill` capture); never validated.
 */
export interface LegacyTestitSnapshot {
  results?: Array<{ testId?: unknown; score?: unknown; isValid?: unknown }>;
}

/** `care_checkins`: aggregate signals only — never an individual mood or note. */
export interface CareCheckinsEvidence {
  wellbeingScore: number;
  averageMood: number;
  moodTrend: 'up' | 'down' | 'stable';
  summary: NonNullable<TalentCareDetail['checkinsSummary']>;
}

/**
 * Each kind's payload. `null` inside a CAPTURED item means the reader found
 * nothing (e.g. no CheckUp calculated yet) — a withheld or redacted item has
 * `payload: null` too, and is told apart by `disposition`, never by payload.
 *
 * `checkup_results` is the CANONICAL form: no `pitchVideoAnalysis.playbackUrl`
 * (the retained pitch video is served through `getRetainedBlobUrl`).
 *
 * ── Why every `entitled` flag is `Omit`ted, and every nullable re-tightened ──
 *
 * These are the READER slices, not the drawer DTOs (W5.8 · W8.D). A reader
 * only ever RUNS when the scope is granted — that is the whole point of
 * deciding consent before reading — and a withheld kind is stored as a
 * `withheld` marker whose payload is `null`, told apart by the item's
 * `disposition`. So the drawer's `entitled: false` branch, with its nulls and
 * empty lists, describes a state a capture never holds: carrying the flag here
 * would invite a reader to ask a captured payload whether it was withheld and
 * always get `true` back. Mirrors `TalentCheckupProgress` / `TalentMobilityPlan`
 * / `TalentAdaptationProgress` / `TalentCareOverview` on the server.
 */
export interface EvidencePayloadByKind {
  checkup_results: CheckUpResults | null;
  checkup_progress: Omit<TalentCheckupDetail, 'entitled' | 'steps' | 'calculation'> & {
    steps: NonNullable<TalentCheckupDetail['steps']>;
  };
  mobility_plan: Omit<
    TalentMobilityDetail,
    'entitled' | 'householdEntitled' | 'householdMembers' | 'setupCompleted' | 'overall'
  > & {
    setupCompleted: boolean;
    overall: NonNullable<TalentMobilityDetail['overall']>;
  };
  adaptation_progress: Omit<
    TalentAdaptationDetail,
    'entitled' | 'overall' | 'miles' | 'streakDays'
  > & {
    overall: NonNullable<TalentAdaptationDetail['overall']>;
    miles: number;
    streakDays: number;
  };
  care_overview: Pick<
    TalentCareDetail,
    'commitment' | 'commitmentHistory' | 'baseline' | 'pillars' | 'checkpoints'
  > & { overall: NonNullable<TalentCareDetail['overall']> };
  testit_results: TestitBusinessOverview | LegacyTestitSnapshot;
  passport_evidence: Omit<TalentEvidenceDetail, 'entitled'>;
  pitch_video: EvidenceFilePayload | null;
  cv: EvidenceFilePayload | null;
  household: TalentMobilityDetail['householdMembers'];
  care_checkins: CareCheckinsEvidence;
  /** `[]` when the journey has no document with a stored file. */
  mobility_documents: EvidenceMobilityDocumentPayload[];
}

/** Narrow an item's payload by its kind. Pure typing — no runtime check. */
export function evidencePayloadOf<K extends EvidenceKind>(
  item: EvidenceItemView & { kind: K }
): EvidencePayloadByKind[K] | null {
  return item.payload as EvidencePayloadByKind[K] | null;
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class EngagementEvidenceService {
  constructor(private readonly client: TonnieClient) {}

  /** Every capture this company holds on the talent, newest first. */
  listForTalent(
    talentId: string,
    options?: { journeyId?: string }
  ): Promise<EvidenceCaptureSummary[]> {
    return this.client.get<EvidenceCaptureSummary[]>(
      API_ENDPOINTS.EVIDENCE.TALENT_CAPTURES(talentId),
      {
        params: options?.journeyId ? { journeyId: options.journeyId } : undefined,
      }
    );
  }

  /** An explicit capture on a LIVE relationship (409 when archived or ended). */
  captureOnDemand(
    talentId: string,
    options?: { journeyId?: string }
  ): Promise<EvidenceCaptureView> {
    return this.client.post<EvidenceCaptureView>(
      API_ENDPOINTS.EVIDENCE.TALENT_CAPTURES(talentId),
      options?.journeyId ? { journeyId: options.journeyId } : {}
    );
  }

  /** One capture, every item (withheld markers included), presented for the caller. */
  getCapture(captureId: string): Promise<EvidenceCaptureView> {
    return this.client.get<EvidenceCaptureView>(API_ENDPOINTS.EVIDENCE.CAPTURE(captureId));
  }

  /** At decision · current, per item — a neutral comparison, never a direction. */
  getDiff(captureId: string): Promise<EvidenceDiffView> {
    return this.client.get<EvidenceDiffView>(API_ENDPOINTS.EVIDENCE.DIFF(captureId));
  }

  /** Every TestIt verdict the company recorded on the talent; the current one flagged. */
  /** `current` is the named relationship's live verdict — the drawer's journey (X8). */
  getVerdictHistory(talentId: string, journeyId: string): Promise<VerdictHistoryView> {
    return this.client.get<VerdictHistoryView>(API_ENDPOINTS.EVIDENCE.VERDICT_HISTORY(talentId), {
      params: { journeyId },
    });
  }

  /**
   * A short-lived URL for a retained file. EVERY call is an audited read on the
   * server — request it when the viewer asks to play/open the file, not on
   * render, and never cache it.
   */
  getRetainedBlobUrl(captureId: string, blobRefId: string): Promise<EvidenceBlobUrlView> {
    return this.client.get<EvidenceBlobUrlView>(
      API_ENDPOINTS.EVIDENCE.BLOB_URL(captureId, blobRefId)
    );
  }

  /** The controller's redaction of one item — the one change a record admits. */
  redactItem(
    captureId: string,
    itemId: string,
    reason: EvidenceRedactionReason
  ): Promise<EvidenceItemView> {
    return this.client.post<EvidenceItemView>(API_ENDPOINTS.EVIDENCE.REDACT(captureId, itemId), {
      reason,
    });
  }
}

export function createEngagementEvidenceService(client: TonnieClient): EngagementEvidenceService {
  return new EngagementEvidenceService(client);
}
